import { useRef, useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { ChatMessage } from './ChatMessage'
import { ChatStatus } from './ChatStatus'
import { ScrollToBottomButton } from './ScrollToBottomButton'
import { ReferencesModal } from './ReferencesModal'
import analyticsService from '../../services/analytics/analytics'

export const ChatMessages = ({
  messages,
  isStreaming,
  loading,
  updateCounter,
  assistantAnimationState,
}) => {
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true)
  const [chatInputHeight, setChatInputHeight] = useState(0)
  const [referencesModal, setReferencesModal] = useState({
    isOpen: false,
    sentenceId: null,
    messageId: null,
    conversationId: null,
    sentenceText: null,
  })
  const messagesEndRef = useRef(null)
  const scrollContainerRef = useRef(null)
  const prevMessagesLengthRef = useRef(0)
  const lastMessageRoleRef = useRef(null)

  const handleSentenceClick = useCallback((sentenceId, messageId, conversationId, sentenceText) => {
    // Track sentence click analytics
    analyticsService.trackEvent('sentence_click', {
      sentence_id: sentenceId,
      message_id: messageId,
      conversation_id: conversationId,
      sentence_text: sentenceText,
    })

    setReferencesModal({
      isOpen: true,
      sentenceId,
      messageId,
      conversationId,
      sentenceText,
    })
  }, [])

  // Measure the fixed chat input height to adjust scroll detection
  useEffect(() => {
    const measureChatInputHeight = () => {
      // Find the fixed chat input container by its classes
      const chatInputContainer = document.querySelector('.glassmorphism-footer')
      if (chatInputContainer) {
        const height = chatInputContainer.offsetHeight
        setChatInputHeight(height)
      }
    }

    // Initial measurement
    measureChatInputHeight()

    // Re-measure when window is resized (responsive padding might change height)
    const handleResize = () => measureChatInputHeight()
    window.addEventListener('resize', handleResize)

    // Use MutationObserver to detect if the chat input content changes height
    const observer = new MutationObserver(measureChatInputHeight)
    const chatInputContainer = document.querySelector('.glassmorphism-footer')
    if (chatInputContainer) {
      observer.observe(chatInputContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style'],
      })
    }

    return () => {
      window.removeEventListener('resize', handleResize)
      observer.disconnect()
    }
  }, []) // Run on mount and when messages change (input might appear/disappear)

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return

    const container = scrollContainerRef.current
    const { scrollTop, scrollHeight, clientHeight } = container

    // Account for fixed chat input height in scroll detection
    // The user is considered "at bottom" when they can see content above the fixed input
    const adjustedThreshold = chatInputHeight + 50 // Extra 50px buffer for better UX
    const isAtBottom = scrollHeight - clientHeight - scrollTop <= adjustedThreshold

    setShouldAutoScroll(isAtBottom)
  }, [chatInputHeight])

  const scrollToBottom = useCallback(
    (forceScroll = false) => {
      if (messagesEndRef.current && (shouldAutoScroll || forceScroll)) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
      }
    },
    [shouldAutoScroll]
  )

  useEffect(() => {
    // Check if a new user message was just added
    const lastMessage = messages[messages.length - 1]
    const isNewUserMessage =
      messages.length > prevMessagesLengthRef.current &&
      lastMessage?.role === 'user' &&
      lastMessageRoleRef.current !== 'user'

    // Check if streaming just started (new assistant message appeared)
    const isNewAssistantMessage =
      messages.length > prevMessagesLengthRef.current &&
      lastMessage?.role === 'assistant' &&
      lastMessageRoleRef.current === 'user'

    if (isNewUserMessage || isNewAssistantMessage) {
      // Force scroll to bottom when user sends a message or assistant starts responding
      setShouldAutoScroll(true)
      scrollToBottom(true)
    } else if (
      shouldAutoScroll &&
      (isStreaming || messages.length !== prevMessagesLengthRef.current)
    ) {
      // Continue auto-scrolling during streaming or when messages update
      scrollToBottom()
    }

    // Update refs for next comparison
    prevMessagesLengthRef.current = messages.length
    lastMessageRoleRef.current = lastMessage?.role || null
  }, [messages, updateCounter, scrollToBottom, shouldAutoScroll, isStreaming])

  return (
    <div className="relative flex-1 flex flex-col overflow-hidden">
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 w-full overflow-y-auto min-h-0 custom-scrollbar bg-dark-primary"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="space-y-6 pt-32"
            style={{
              paddingBottom: `${Math.max(chatInputHeight + 32, 128)}px`, // Dynamic padding: chat input height + 32px buffer, minimum 128px
            }}
          >
            {messages.map((message, index) => (
              <ChatMessage
                key={`msg-${index}`}
                message={message}
                isStreaming={isStreaming}
                isLastMessage={index === messages.length - 1}
                onSentenceClick={handleSentenceClick}
                assistantAnimationState={assistantAnimationState}
              />
            ))}

            {loading && !isStreaming && <ChatStatus type="loading" />}

            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>

      <ScrollToBottomButton
        show={!shouldAutoScroll && messages.length > 0}
        onClick={() => {
          setShouldAutoScroll(true)
          scrollToBottom(true)
        }}
      />

      <ReferencesModal
        isOpen={referencesModal.isOpen}
        onClose={() => setReferencesModal({ ...referencesModal, isOpen: false })}
        sentenceId={referencesModal.sentenceId}
        messageId={referencesModal.messageId}
        conversationId={referencesModal.conversationId}
        sentenceText={referencesModal.sentenceText}
      />
    </div>
  )
}

ChatMessages.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      role: PropTypes.oneOf(['user', 'assistant']).isRequired,
      content: PropTypes.string.isRequired,
      isProgress: PropTypes.bool,
      isError: PropTypes.bool,
      conversationId: PropTypes.string,
      messageId: PropTypes.string,
    })
  ).isRequired,
  isStreaming: PropTypes.bool,
  loading: PropTypes.bool,
  updateCounter: PropTypes.number,
  assistantAnimationState: PropTypes.oneOf(['idle', 'thinking', 'processing', 'streaming']),
}

ChatMessages.defaultProps = {
  isStreaming: false,
  loading: false,
  updateCounter: 0,
  assistantAnimationState: 'idle',
}
